import {post_create_Api} from 'apis'
import {getId, getType} from 'apis/model/base'
import {createValue} from 'components/form/utils'
import {Field, Formik, useFormikContext} from 'formik'
import _ from 'lodash'
import useAsyncAction from 'modules/asyncCache/useAsyncAction'
import useTranslate from 'modules/local/useTranslate'
import {useHistory} from 'modules/navigation/useRouter'
import React, {useCallback, useContext, useMemo, useRef} from 'react'
import {nest} from 'recompose'
import {NotificationActionTypes} from 'views/AppLocals/factory/createNotification'
import {NullAsDefault} from 'views/Shared'
import {messageOnError} from '../../../views/AppLocals/factory/createErrorEvent'
import {LayoutContext} from '../../layouts/Default/LayoutContext'
import MentionDataContext, {MentionDataProvider} from '../MentionDataContext'
import MegaTextarea from './MegaTextarea'
import {PostPrivacyTypes} from './PostPrivacy'
import LookupField from '../../LookupField'
import {PostFormContext} from '../PostForm/PostFormContext'
import PostSettingForm from '../PostForm/PostSettingForm'
import {useMessageNotification} from "../../../views/AppLocals/factory/createMessageNotification";
import classNames from "classnames";
import {Button} from "antd";

// ─── Tab selector: Chia sẻ / Ý nguyện ───────────────────────────────────────

const PostTypeTabs = ({value, onChange}) => {
    const t = useTranslate()
    const tabs = [
        {value: 'post',   labelKey: 'news post'},
        {value: 'prayer', labelKey: 'prayer intention'},
    ]
    return (
        <div
            style={{ padding: 5, borderRadius: 10 }}
            className="w-fit flex items-center gap-1 bg-slate-100">
            {tabs.map((tab) => {
                const active = value === tab.value
                return (
                    <button
                        key={tab.value}
                        type="button"
                        onClick={() => onChange(tab.value)}
                        style={{
                            padding: '0.25rem 0.75rem', borderRadius: '0.375rem',
                            fontSize: 14, fontWeight: active ? 600 : 500,
                            color: active ? 'var(--color-secondary-600)' : '#64748b',
                        }}
                        className={classNames(
                            'tracking-wide whitespace-no-wrap transition-all duration-300',
                            active ? 'background' : 'hover:bg-slate-200'
                        )}>
                        {t(tab.labelKey)}
                    </button>
                )
            })}
        </div>
    )
}

// ─── PostForm (inner) ─────────────────────────────────────────────────────────

const PostForm = ({onCancel}) => {
    const t = useTranslate()
    const {isLg, isSm} = useContext(LayoutContext)
    const {handleSubmit, isValid, values, handleChange} = useFormikContext()
    const {push = NullAsDefault} = useContext(MentionDataContext)

    const isPrayer = values?.post_type === 'prayer'

    return (
        <div className="flex flex-col gap-4">

            {/* ── Header ── */}
            <div className="flex flex-col gap-1">
                <div className="mb-4 pb-3 border-b border-color-100 flex items-center justify-between" >
                    {!isLg ? (
                        <button
                            type="button"
                            onClick={onCancel}
                            style={{
                                fontSize: 13,
                                color: 'var(--color-text-400, #888)',
                                background: 'none',
                                border: 'none',
                                cursor: 'pointer',
                                minWidth: 48,
                                textAlign: 'left',
                                padding: '4px 0',
                            }}>
                            {t('close')}
                        </button>
                    ) : <div style={{minWidth: 48}}/>}

                    <span
                        style={{flex: 1}}
                        className="text-center font-bold text-base md:text-lg text-color-000 uppercase tracking-wider">
                        {isPrayer ? t('intention') : t('posts')}
                    </span>

                    {/* Nút Đăng — chỉ hiện trên mobile, desktop dùng footer */}
                    {!isLg ? (
                        <button
                            type="button"
                            onClick={() => handleSubmit()}
                            disabled={!isValid}
                            className="flex flex-center"
                            style={{
                                fontSize: 13,
                                fontWeight: 700,
                                background: isValid ? 'var(--color-primary)' : 'var(--background-200, #e0e0e0)',
                                color: isValid ? '#fff' : 'var(--color-text-400, #aaa)',
                                border: 'none',
                                borderRadius: '0.5rem',
                                padding: '7px 18px',
                                cursor: isValid ? 'pointer' : 'not-allowed',
                                transition: 'all .15s',
                                minWidth: 72,
                                whiteSpace: 'nowrap',
                            }}>
                            {t('publish a post')}
                        </button>
                    ) : (
                        <div style={{minWidth: 48}}/>
                    )}
                </div>
                {/* ── Author + Channel row ── */}
                <PostSettingForm/>
            </div>

            {/* ── Tabs — kéo ra 2 bên bù lại padding của wrapper ── */}
            <PostTypeTabs
                value={values?.post_type}
                onChange={(val) => handleChange(createValue('post_type', val))}
            />

            {/* ── Prayer category ── */}
            {isPrayer && (
                <div >
                    <LookupField
                        createIfNotExists={true}
                        defaultValue={values?.prayer_category}
                        apiPath="/lookup/prayer_category/gets"
                        placeholder={t('choose prayer type')}
                        name="prayer_category"
                        renderOption={(o, i) => (
                            <div key={i} className="flex flex-col justify-center flex-1">
                                <span className="font-semibold text-sm text-color-000 tracking-wide leading-snug">
                                    {o?.label}
                                </span>
                                {o?.description && (
                                    <span className="text-xs text-color-300 italic tracking-wider leading-snug">
                                        {o?.description}
                                    </span>
                                )}
                            </div>
                        )}
                        handleChange={(e) => {
                            handleChange(createValue('prayer_category', e.target.value))
                        }}
                    />
                </div>
            )}

            {/* ── Textarea ── */}
            <div style={{flex: 1}}>
                <Field name="description">
                    {({field}) => (
                        <MegaTextarea
                            {...field}
                            rows={isSm ? 6 : 4}
                            placeholder={
                                isPrayer
                                    ? t('share your prayer intention')
                                    : t('share your thoughts')
                            }
                            style={{
                                outline: 'none',
                                resize: 'none',
                                fontSize: isSm ? 16 : 15,
                            }}
                            wrapperClassName="flex flex-col gap-3 rounded-xl border border-color-100 p-3"
                            fieldNames={{embed_data: 'embed_data', description: 'description'}}
                            onMentioned={(mention) => push(mention)}
                        />
                    )}
                </Field>
            </div>

            {/* ── Footer: desktop only ── */}
            {isLg && (
                <div
                    className="flex justify-end gap-3"
                    style={{
                        paddingTop: '1rem',
                        // borderTop: '1px solid var(--border-color-50, #f0f0f0)',
                    }}>
                    <Button
                        color="default" variant="link"
                        onClick={onCancel}
                        className="text-color-300">
                        {t('close')}
                    </Button>
                    <button
                        type="button"
                        onClick={() => handleSubmit()}
                        disabled={!isValid}
                        style={{
                            fontSize: 13,
                            color: isValid ? '#fff' : 'var(--color-text-400, #aaa)',
                            border: 'none',
                            borderRadius: 8,
                            padding: '7px 22px',
                            cursor: isValid ? 'pointer' : 'not-allowed',
                            transition: 'background .15s',
                        }}
                        className={classNames(
                            'font-semibold tracking-wide', isValid ? 'bg-primary hover:bg-primary-800' : 'background-200'
                        )}>
                        {t('publish a post')}
                    </button>
                </div>
            )}
        </div>
    )
}

// ─── CreatePostForm ───────────────────────────────────────────────────────────

const CreatePostForm = ({initialValues, onCancel = NullAsDefault}) => {
    const t = useTranslate()
    const history = useHistory()
    const {options, current} = useContext(PostFormContext)
    const submittingRef = useRef(false)
    const {toString = NullAsDefault} = useContext(MentionDataContext)
    const {successMessage, errorMessage} = useMessageNotification()

    const _initialValues = useMemo(() => {
        const {container, creator, privacy} = current ?? {}
        const defaultChannelOption = container
            ? options.find((o) => o.value === getId(container)) ?? null
            : _.first(options) ?? null
        return {
            containers: options,
            privacy: privacy ?? PostPrivacyTypes.public.value,
            post_type: 'post',
            ...(initialValues ?? {}),
            container: defaultChannelOption ?? undefined,
            owner: creator
                ? {
                    value: getId(creator),
                    data: creator,
                    id: getId(creator),
                    type: getType(creator),
                    label: creator?.name ?? creator?.username ?? '',
                }
                : undefined,
        }
    }, [current, options])

    const onSuccess = (__, {response}) => {
        submittingRef.current = false
        onCancel()
        successMessage(NotificationActionTypes.CREATE, t)
        history.push({state: {refreshToken: Date.now()}})
    }

    const onError = (...args) => {
        submittingRef.current = false
        messageOnError(t)(...args)
    }

    const {handleAsyncAction: createPost = NullAsDefault} = useAsyncAction({
        apiInfo: post_create_Api,
        onError,
        onSuccess,
    })

    const onSubmit = ({tags, description, ...rest}) => {
        if (submittingRef.current) return
        submittingRef.current = true
        const _tags = _.isArray(tags) ? Array.from(tags) : undefined
        createPost(
            {
                ...rest,
                tags: _tags ? _tags.join(',') : undefined,
                description: toString(description),
            },
            {id: rest.container_id, prop: rest.container_type}
        )
    }

    const initialErrors = {
        description: t('min length 2 characters'),
    }

    const validate = useCallback((values) => {
        const errors = {}
        const {description, post_type, prayer_category} = values ?? {}
        if (post_type === 'prayer' && _.isNil(prayer_category ?? undefined)) {
            errors.prayer_category = t('required field')
        }
        _.forEach(['owner', 'container'], (name) => {
            if (_.isNil(_.get(values, name) ?? undefined)) {
                errors[name] = t('required field')
            }
        })
        if (String(description ?? '').trim().length < 2) {
            errors.description = initialErrors.description
        }
        return errors
    }, [])

    return (
        <Formik
            onSubmit={(values) => {
                const {
                    owner, container, containers,
                    default_owner_id, default_container_id,
                    isLoading,
                    ...rest
                } = values ?? {}
                onSubmit(
                    _.omitBy(
                        {
                            ...rest,
                            owner_id: getId(owner?.data),
                            owner_type: getType(owner?.data),
                            container_id: getId(container?.data),
                            container_type: getType(container?.data),
                        },
                        _.isUndefined
                    )
                )
            }}
            validate={validate}
            enableReinitialize={true}
            initialErrors={initialErrors}
            initialValues={_initialValues}>
            <PostForm onCancel={onCancel}/>
        </Formik>
    )
}

export default nest(MentionDataProvider, CreatePostForm)